import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { useWindowStore } from '@/stores/windowStore';
import { DESKTOP_APPS, getAppByRoute } from '@/config/desktopApps';
import type { DesktopApp } from '@/config/desktopApps';
import { DesktopIcon } from './DesktopIcon';
import { WindowManager } from './WindowManager';
import { Taskbar } from './Taskbar';
import { RabbitHoleTransition } from './RabbitHoleTransition';
import { LoginTerminal } from './LoginTerminal';
import { GlitchOverlay } from './GlitchOverlay';

const UNAUTH_ALLOWED_ROUTES = new Set(['/','/login','/register','/terms','/privacy']);

export function Desktop() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, profile } = useAuthStore();
  const { hasEnteredRabbitHole, isTransitioning, enterRabbitHole } = useThemeStore();
  const { openWindow, windows, closeWindow } = useWindowStore();
  const [showLoginTerminal, setShowLoginTerminal] = useState(false);
  const [isGlitching, setIsGlitching] = useState(false);
  const isAuthenticated = !!session;
  const isAdmin = profile?.role === 'admin' || profile?.role === 'moderator';
  const isMember = isAuthenticated && profile?.pill_choice === 'red';

  useEffect(() => {
    if (isMember && !hasEnteredRabbitHole) {
      enterRabbitHole();
    }
  }, [isMember, hasEnteredRabbitHole, enterRabbitHole]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (location.pathname === '/login' || location.pathname === '/register') {
      navigate('/');
    }
  }, [isAuthenticated, location.pathname, navigate]);

  useEffect(() => {
    if (isAuthenticated) return;
    if (!UNAUTH_ALLOWED_ROUTES.has(location.pathname)) {
      navigate('/');
    }
  }, [isAuthenticated, location.pathname, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    for (const win of windows) {
      if (win.appId === 'login' || win.appId === 'register') {
        closeWindow(win.id);
      }
    }
  }, [isAuthenticated, windows, closeWindow]);

  const visibleApps = DESKTOP_APPS.filter((app) => {
    if (app.hideFromDesktopIcon) return false;

    if (!isAuthenticated) {
      return app.id === 'login';
    }

    if (app.authRequired && !isAuthenticated) return false;
    if (app.memberOnly && !hasEnteredRabbitHole) return false;
    if (app.normieOnly && hasEnteredRabbitHole) return false;
    if (app.hideWhenAuthenticated && isAuthenticated) return false;
    if (app.adminOnly && !isAdmin) return false;
    return true;
  });

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/') return;

    const app = getAppByRoute(currentPath);
    if (!app) return;

    if (app.authRequired && !isAuthenticated) return;
    if (app.memberOnly && !hasEnteredRabbitHole) return;
    if (app.normieOnly && hasEnteredRabbitHole) return;
    if (app.hideWhenAuthenticated && isAuthenticated) return;

    openWindow(app.id, app.label);
  }, [location.pathname, isAuthenticated, hasEnteredRabbitHole, openWindow]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (windows.length === 0 && location.pathname !== '/') {
      navigate('/');
    }
  }, [isAuthenticated, windows.length, location.pathname, navigate]);

  // Auto-open LoginTerminal for normie users who haven't chosen a pill yet
  useEffect(() => {
    if (isAuthenticated && profile && !profile.pill_choice && !showLoginTerminal) {
      setShowLoginTerminal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, profile?.pill_choice]);

  const handleOpenApp = (app: DesktopApp) => {
    if (app.id === 'login') {
      setIsGlitching(true);
      return;
    }

    const resolveSize = (val: string, axis: 'w' | 'h'): number => {
      const vw = window.innerWidth;
      const vh = window.innerHeight - 40;
      const minMatch = val.match(/min\(\s*([\d.]+)(vw|vh|px)\s*,\s*([\d.]+)(vw|vh|px)\s*\)/i);
      if (minMatch) {
        const toPixels = (n: number, unit: string) =>
          unit === 'vw' ? (n / 100) * vw : unit === 'vh' ? (n / 100) * vh : n;
        return Math.min(toPixels(parseFloat(minMatch[1]), minMatch[2]), toPixels(parseFloat(minMatch[3]), minMatch[4]));
      }
      const simple = val.match(/([\d.]+)(vw|vh|px)/i);
      if (simple) {
        const n = parseFloat(simple[1]);
        if (simple[2] === 'vw') return (n / 100) * vw;
        if (simple[2] === 'vh') return (n / 100) * vh;
        return n;
      }
      return axis === 'w' ? 900 : 600;
    };

    openWindow(app.id, app.label, resolveSize(app.width, 'w'), resolveSize(app.height, 'h'));
    if (app.route) navigate(app.route);
  };

  const backgroundClass = hasEnteredRabbitHole
    ? 'bg-gradient-to-br from-[#0d0208] via-[#1a1a1a] to-[#003b00]'
    : 'bg-gradient-to-br from-[#008080] to-[#006666]';

  const logoOpacity = hasEnteredRabbitHole ? 'opacity-20' : 'opacity-40';
  const overlayIntensity = {
    crt: hasEnteredRabbitHole ? 'opacity-80' : 'opacity-100',
    noise: hasEnteredRabbitHole ? 'opacity-15' : 'opacity-100',
  };

  return (
    <div className={`h-screen w-screen ${backgroundClass} relative overflow-hidden transition-all duration-1000`}>
      <div className={`crt-overlay ${overlayIntensity.crt}`} />
      <div className={`noise-overlay ${overlayIntensity.noise}`} />
      <div className="win98-wallpaper" />

      {isAuthenticated && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-5">
          <img
            src="/images/logoerhbestwhte.png"
            alt="ElectricRabbitHole Logo"
            className={`w-32 h-32 object-contain ${logoOpacity} transition-opacity duration-1000`}
          />
        </div>
      )}

      <div className="absolute top-4 left-4 grid grid-cols-1 gap-2 z-10">
        {visibleApps.map((app) => (
          <DesktopIcon key={app.id} app={app} onOpen={handleOpenApp} />
        ))}
      </div>

      <WindowManager />
      <Taskbar />

      {isTransitioning && <RabbitHoleTransition />}

      {isGlitching && (
        <GlitchOverlay onComplete={() => { setIsGlitching(false); setShowLoginTerminal(true); }} />
      )}

      {showLoginTerminal && (
        <LoginTerminal onClose={() => setShowLoginTerminal(false)} />
      )}
    </div>
  );
}
